import type { FC } from 'react'

import styled from '@emotion/styled'

import Typography from '@segment/matcha/components/Typography'

import AccordionBase, * as Accordion from '@segment/matcha/recipes/Accordion'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import { useActiveState } from '@segment/utils/useActiveState'

const faqData = [
  {
    question: `What is data cleansing?`,
    answer: `Data cleansing is the process of ensuring data is complete, accurate, and reliable so that it can be used for analysis, decision-making, and reporting. Data cleansing includes correcting outdated or missing information, applying standardized naming conventions to data entries for consistency, deduplicating events, recording data transformations for transparency, and more.`,
  },
  {
    question: `What can happen when I don’t use clean customer data?`,
    answer: `Without properly cleaning data, organizations run the risk of basing strategies and campaigns off inaccurate or misleading information. Duplicate data entries, incomplete fields, and inconsistent naming conventions are common culprits behind “bad data,” which can cost businesses millions of dollars each year in misguided insights and poor decision-making.`,
  },
]

const copy = {
  heading: `Frequently asked questions`,
}

const FaqSection: FC = () => {
  const items = new Array(faqData.length).fill(false)
  const { activeItems, toggle } = useActiveState(items, {
    allowMultipleActive: true,
  })

  return (
    <StyledSectionBase variant="noCopy">
      <Section.Contents>
        <Heading variant="h2">{copy.heading}</Heading>
        <Accordion.JsonLdFaq
          data={faqData.map(({ answer, question }) => {
            return {
              question,
              answer,
            }
          })}
        />
        {activeItems.map((active, index) => (
          <AccordionBase key={`item-${index}`}>
            <Accordion.Header onClick={() => toggle(index)}>
              <Accordion.TitleWrapper>
                <Accordion.Title tag="h3" opened={active}>
                  {faqData[index].question}
                </Accordion.Title>
              </Accordion.TitleWrapper>
              <Accordion.Icon opened={active} />
            </Accordion.Header>
            <Accordion.Body>
              {active && (
                <Accordion.ContentWrapper>
                  <Accordion.Text>{faqData[index].answer}</Accordion.Text>
                </Accordion.ContentWrapper>
              )}
            </Accordion.Body>
          </AccordionBase>
        ))}
      </Section.Contents>
    </StyledSectionBase>
  )
}

const Heading = styled(Typography)`
  padding-bottom: 28px;
  text-align: center;
  margin-top: 0;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    padding-bottom: 44px;
  }
`

const StyledSectionBase = styled(SectionBase)`
  max-width: 1004px;
  overflow-x: unset;

  ${Section.Wrapper} {
    padding-top: 0;
  }

  ${({ theme }) => theme.breakpoints.down(`xs`)} {
    padding-bottom: 40px;
  }
`

export default FaqSection
